import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import {
  ScanFace, CheckCircle, XCircle, RotateCcw, Play, Loader2,
  Users, Camera, ChevronLeft, ChevronRight, Zap, AlertTriangle, Eye
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAGE_SIZE = 20;

function BoundingBoxOverlay({ boundingBox, confidence }) {
  if (!boundingBox) return null;
  const style = {
    position: 'absolute',
    left: `${boundingBox.left * 100}%`,
    top: `${boundingBox.top * 100}%`,
    width: `${boundingBox.width * 100}%`,
    height: `${boundingBox.height * 100}%`,
    border: `2px solid ${confidence >= 80 ? '#22c55e' : confidence >= 60 ? '#f59e0b' : '#ef4444'}`,
    borderRadius: '4px',
    pointerEvents: 'none',
  };
  return <div style={style} />;
}

function ConfidenceBadge({ confidence }) {
  let label, colorClasses;
  if (confidence >= 80) {
    label = 'High Match';
    colorClasses = 'bg-green-100 text-green-800';
  } else if (confidence >= 60) {
    label = 'Medium Match';
    colorClasses = 'bg-amber-100 text-amber-800';
  } else {
    label = 'Low Match';
    colorClasses = 'bg-red-100 text-red-800';
  }
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${colorClasses}`}>
      {label} {confidence.toFixed(1)}%
    </span>
  );
}

function ContactInitials({ name }) {
  const initials = (name || '?')
    .split(' ')
    .map(w => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
  return (
    <div className="w-16 h-16 rounded-full bg-stone-200 flex items-center justify-center text-stone-600 font-semibold text-lg">
      {initials}
    </div>
  );
}

export default function FaceReviewPanel({ eventId }) {
  const { getAuthHeader } = useAuth();

  const [matches, setMatches] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0, total: 0, auto_suggested: 0 });
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(new Set());
  const [actionLoading, setActionLoading] = useState({});
  const [detectLoading, setDetectLoading] = useState(false);
  const [matchLoading, setMatchLoading] = useState(false);
  const [rerunLoading, setRerunLoading] = useState(false);
  const [bulkApproveAllLoading, setBulkApproveAllLoading] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, page_size: PAGE_SIZE };
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await axios.get(`${API}/events/${eventId}/face-review`, {
        params,
        headers: getAuthHeader(),
      });
      setMatches(res.data.data || []);
      setTotal(res.data.total || 0);
      if (res.data.stats) setStats(res.data.stats);
    } catch (err) {
      console.error('Failed to load face matches:', err);
      toast.error('Failed to load face matches');
    } finally {
      setLoading(false);
    }
  }, [eventId, page, statusFilter, getAuthHeader]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    setPage(1);
    setSelected(new Set());
  }, [statusFilter]);

  const handleApprove = async (matchId) => {
    setMatches(prev => prev.map(m => m.match_id === matchId ? { ...m, status: 'approved' } : m));
    setActionLoading(prev => ({ ...prev, [matchId]: 'approve' }));
    try {
      await axios.post(`${API}/events/${eventId}/face-review/${matchId}/approve`, {}, { headers: getAuthHeader() });
      toast.success('Match approved');
      setStats(prev => ({ ...prev, pending: Math.max(0, prev.pending - 1), approved: prev.approved + 1 }));
    } catch (err) {
      setMatches(prev => prev.map(m => m.match_id === matchId ? { ...m, status: 'pending' } : m));
      toast.error('Failed to approve match');
    } finally {
      setActionLoading(prev => { const n = { ...prev }; delete n[matchId]; return n; });
    }
  };

  const handleReject = async (matchId) => {
    setMatches(prev => prev.map(m => m.match_id === matchId ? { ...m, status: 'rejected' } : m));
    setActionLoading(prev => ({ ...prev, [matchId]: 'reject' }));
    try {
      await axios.post(`${API}/events/${eventId}/face-review/${matchId}/reject`, {}, { headers: getAuthHeader() });
      toast.success('Match rejected');
      setStats(prev => ({ ...prev, pending: Math.max(0, prev.pending - 1), rejected: prev.rejected + 1 }));
    } catch (err) {
      setMatches(prev => prev.map(m => m.match_id === matchId ? { ...m, status: 'pending' } : m));
      toast.error('Failed to reject match');
    } finally {
      setActionLoading(prev => { const n = { ...prev }; delete n[matchId]; return n; });
    }
  };

  const handleBulkAction = async (action) => {
    if (selected.size === 0) return;
    setBulkActionLoading(true);
    try {
      const res = await axios.post(`${API}/events/${eventId}/face-review/bulk`, {
        match_ids: Array.from(selected),
        action,
      }, { headers: getAuthHeader() });
      toast.success(`${res.data.processed || selected.size} matches ${action}d`);
      setSelected(new Set());
      await loadData();
    } catch (err) {
      toast.error(`Failed to ${action} selected matches`);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleApproveAllSuggested = async () => {
    const suggestedPending = matches.filter(m => m.auto_suggested && m.status === 'pending');
    const count = stats.auto_suggested || suggestedPending.length;
    if (!window.confirm(`Approve all ${count} auto-suggested matches (\u226580% confidence)?`)) return;
    setBulkApproveAllLoading(true);
    try {
      const ids = suggestedPending.map(m => m.match_id);
      if (ids.length > 0) {
        await axios.post(`${API}/events/${eventId}/face-review/bulk`, {
          match_ids: ids,
          action: 'approve',
        }, { headers: getAuthHeader() });
      }
      toast.success(`Approved ${ids.length} auto-suggested matches`);
      await loadData();
    } catch (err) {
      toast.error('Failed to approve suggested matches');
    } finally {
      setBulkApproveAllLoading(false);
    }
  };

  const handleDetect = async () => {
    setDetectLoading(true);
    try {
      const res = await axios.post(`${API}/events/${eventId}/detect-faces`, {}, { headers: getAuthHeader() });
      toast.success(`Processed ${res.data.processed} photos, found ${res.data.faces_found} faces`);
      await loadData();
    } catch (err) {
      toast.error('Face detection failed');
    } finally {
      setDetectLoading(false);
    }
  };

  const handleMatch = async () => {
    setMatchLoading(true);
    try {
      const res = await axios.post(`${API}/events/${eventId}/match-faces`, {}, { headers: getAuthHeader() });
      toast.success(`Matched ${res.data.matched} faces, ${res.data.new_matches} new matches`);
      await loadData();
    } catch (err) {
      toast.error('Face matching failed');
    } finally {
      setMatchLoading(false);
    }
  };

  const handleRerun = async () => {
    setRerunLoading(true);
    toast.info('Re-running detection and matching. This may take a moment...');
    try {
      const res = await axios.post(`${API}/events/${eventId}/rerun-matching`, {}, { headers: getAuthHeader() });
      const dr = res.data.detect_result || {};
      const mr = res.data.match_result || {};
      toast.success(`Done: ${dr.faces_found || 0} faces detected, ${mr.new_matches || 0} new matches`);
      await loadData();
    } catch (err) {
      toast.error('Re-run failed');
    } finally {
      setRerunLoading(false);
    }
  };

  const toggleSelect = (matchId) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(matchId)) next.delete(matchId);
      else next.add(matchId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const pendingIds = matches.filter(m => m.status === 'pending').map(m => m.match_id);
    if (pendingIds.every(id => selected.has(id))) {
      setSelected(new Set());
    } else {
      setSelected(new Set(pendingIds));
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const showFrom = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const showTo = Math.min(page * PAGE_SIZE, total);
  const pendingOnPage = matches.filter(m => m.status === 'pending');

  const filterOptions = [
    { key: 'all', label: 'All' },
    { key: 'pending', label: 'Pending' },
    { key: 'approved', label: 'Approved' },
    { key: 'rejected', label: 'Rejected' },
  ];

  // Empty state
  if (!loading && stats.total === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <ScanFace className="w-16 h-16 text-stone-300 mb-4" />
        <h3 className="text-lg font-semibold text-stone-700 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          No face matches yet
        </h3>
        <p className="text-stone-500 mb-6 max-w-md">
          Run detection and matching to find faces in event photos
        </p>
        <div className="flex gap-3">
          <button
            onClick={handleDetect}
            disabled={detectLoading}
            className="inline-flex items-center gap-2 px-4 py-2 bg-stone-800 text-white rounded-lg hover:bg-stone-700 disabled:opacity-50 transition-colors"
          >
            {detectLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
            Run Detection
          </button>
          <button
            onClick={handleMatch}
            disabled={matchLoading}
            className="inline-flex items-center gap-2 px-4 py-2 bg-stone-800 text-white rounded-lg hover:bg-stone-700 disabled:opacity-50 transition-colors"
          >
            {matchLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />}
            Run Matching
          </button>
        </div>
      </div>
    );
  }

  // All reviewed state
  if (!loading && stats.total > 0 && stats.pending === 0 && statusFilter === 'pending') {
    return (
      <div className="space-y-6">
        <StatsBar stats={stats} />
        <ActionBar
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          filterOptions={filterOptions}
          onDetect={handleDetect}
          detectLoading={detectLoading}
          onMatch={handleMatch}
          matchLoading={matchLoading}
          onRerun={handleRerun}
          rerunLoading={rerunLoading}
          showApproveAll={false}
        />
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <CheckCircle className="w-16 h-16 text-green-400 mb-4" />
          <h3 className="text-lg font-semibold text-stone-700 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            All matches reviewed!
          </h3>
          <p className="text-stone-500">
            Great work — all face matches have been processed
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Bar */}
      <StatsBar stats={stats} />

      {/* Action Bar */}
      <ActionBar
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        filterOptions={filterOptions}
        onDetect={handleDetect}
        detectLoading={detectLoading}
        onMatch={handleMatch}
        matchLoading={matchLoading}
        onRerun={handleRerun}
        rerunLoading={rerunLoading}
        showApproveAll={statusFilter === 'pending' && stats.auto_suggested > 0}
        onApproveAllSuggested={handleApproveAllSuggested}
        bulkApproveAllLoading={bulkApproveAllLoading}
      />

      {/* Bulk Actions Bar */}
      {selected.size > 0 && (
        <div className="sticky top-0 z-10 bg-stone-800 text-white rounded-lg px-4 py-3 flex items-center justify-between">
          <span className="text-sm font-medium">{selected.size} selected</span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleBulkAction('approve')}
              disabled={bulkActionLoading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 rounded-md text-sm font-medium disabled:opacity-50 transition-colors"
            >
              {bulkActionLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />}
              Approve Selected
            </button>
            <button
              onClick={() => handleBulkAction('reject')}
              disabled={bulkActionLoading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-700 rounded-md text-sm font-medium disabled:opacity-50 transition-colors"
            >
              {bulkActionLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />}
              Reject Selected
            </button>
            <button
              onClick={() => setSelected(new Set())}
              className="px-3 py-1.5 bg-stone-600 hover:bg-stone-500 rounded-md text-sm font-medium transition-colors"
            >
              Clear Selection
            </button>
          </div>
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-stone-400" />
        </div>
      ) : matches.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Eye className="w-12 h-12 text-stone-300 mb-3" />
          <p className="text-stone-500">No matches found for this filter</p>
        </div>
      ) : (
        <>
          {/* Select All checkbox for pending */}
          {pendingOnPage.length > 0 && (
            <div className="flex items-center gap-2 px-1">
              <input
                type="checkbox"
                checked={pendingOnPage.length > 0 && pendingOnPage.every(m => selected.has(m.match_id))}
                onChange={toggleSelectAll}
                className="w-4 h-4 rounded border-stone-300 text-stone-800 focus:ring-stone-500"
              />
              <span className="text-sm text-stone-500">Select all on page</span>
            </div>
          )}

          {/* Match Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {matches.map(match => (
              <MatchCard
                key={match.match_id}
                match={match}
                isSelected={selected.has(match.match_id)}
                onToggleSelect={() => toggleSelect(match.match_id)}
                onApprove={() => handleApprove(match.match_id)}
                onReject={() => handleReject(match.match_id)}
                actionLoading={actionLoading[match.match_id]}
              />
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-2">
              <p className="text-sm text-stone-500">
                Showing {showFrom}-{showTo} of {total}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-stone-200 rounded-md hover:bg-stone-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </button>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-stone-200 rounded-md hover:bg-stone-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function StatsBar({ stats }) {
  const items = [
    { label: 'Pending Review', value: stats.pending, bg: 'bg-amber-50', text: 'text-amber-800', border: 'border-amber-200', icon: AlertTriangle },
    { label: 'Approved', value: stats.approved, bg: 'bg-green-50', text: 'text-green-800', border: 'border-green-200', icon: CheckCircle },
    { label: 'Rejected', value: stats.rejected, bg: 'bg-red-50', text: 'text-red-800', border: 'border-red-200', icon: XCircle },
    { label: 'Auto-Suggested (\u226580%)', value: stats.auto_suggested, bg: 'bg-blue-50', text: 'text-blue-800', border: 'border-blue-200', icon: Zap },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {items.map(item => (
        <div key={item.label} className={`${item.bg} ${item.border} border rounded-xl p-4`}>
          <div className="flex items-center gap-2 mb-1">
            <item.icon className={`w-4 h-4 ${item.text}`} />
            <span className={`text-xs font-medium ${item.text}`}>{item.label}</span>
          </div>
          <p className={`text-2xl font-bold ${item.text}`} style={{ fontFamily: 'Manrope, sans-serif' }}>
            {item.value}
          </p>
        </div>
      ))}
    </div>
  );
}

function ActionBar({
  statusFilter, setStatusFilter, filterOptions,
  onDetect, detectLoading, onMatch, matchLoading,
  onRerun, rerunLoading, showApproveAll, onApproveAllSuggested, bulkApproveAllLoading,
}) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      {/* Status filter segmented control */}
      <div className="inline-flex bg-stone-100 rounded-lg p-0.5">
        {filterOptions.map(opt => (
          <button
            key={opt.key}
            onClick={() => setStatusFilter(opt.key)}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              statusFilter === opt.key
                ? 'bg-white text-stone-900 shadow-sm'
                : 'text-stone-500 hover:text-stone-700'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <div className="flex-1" />

      {/* Action buttons */}
      <div className="flex items-center gap-2 flex-wrap">
        {showApproveAll && (
          <button
            onClick={onApproveAllSuggested}
            disabled={bulkApproveAllLoading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {bulkApproveAllLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
            Approve All Suggested
          </button>
        )}
        <button
          onClick={onDetect}
          disabled={detectLoading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-stone-200 rounded-lg text-sm font-medium text-stone-700 hover:bg-stone-50 disabled:opacity-50 transition-colors"
        >
          {detectLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
          Run Detection
        </button>
        <button
          onClick={onMatch}
          disabled={matchLoading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-stone-200 rounded-lg text-sm font-medium text-stone-700 hover:bg-stone-50 disabled:opacity-50 transition-colors"
        >
          {matchLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Users className="w-3.5 h-3.5" />}
          Run Matching
        </button>
        <button
          onClick={onRerun}
          disabled={rerunLoading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-stone-200 rounded-lg text-sm font-medium text-stone-700 hover:bg-stone-50 disabled:opacity-50 transition-colors"
        >
          {rerunLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
          Re-run All
        </button>
      </div>
    </div>
  );
}

function MatchCard({ match, isSelected, onToggleSelect, onApprove, onReject, actionLoading }) {
  const isPending = match.status === 'pending';
  const isApproved = match.status === 'approved';
  const isRejected = match.status === 'rejected';

  return (
    <div className={`bg-white rounded-xl border ${isSelected ? 'border-stone-400 ring-1 ring-stone-300' : 'border-stone-200'} p-4 relative transition-all`}>
      {/* Checkbox for pending items */}
      {isPending && (
        <div className="absolute top-3 left-3 z-10">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={onToggleSelect}
            className="w-4 h-4 rounded border-stone-300 text-stone-800 focus:ring-stone-500"
          />
        </div>
      )}

      <div className="flex gap-4">
        {/* Left side - Event photo */}
        <div className="flex-shrink-0 flex flex-col items-center gap-2">
          <div className="relative" style={{ width: 200 }}>
            <img
              src={match.photo_url}
              alt={match.photo_display_filename || 'Event photo'}
              className="w-full rounded-lg object-cover"
              style={{ maxHeight: 160 }}
            />
            <BoundingBoxOverlay
              boundingBox={match.bounding_box}
              confidence={match.confidence}
            />
          </div>
          {match.face_crop_url && (
            <img
              src={match.face_crop_url}
              alt="Face crop"
              className="w-24 h-24 rounded-lg object-cover border border-stone-200"
            />
          )}
          <p className="text-xs text-stone-400 truncate max-w-[200px]" title={match.photo_display_filename}>
            {match.photo_display_filename || 'Unknown photo'}
          </p>
        </div>

        {/* Right side - Contact info */}
        <div className="flex-1 flex flex-col justify-between min-w-0">
          <div>
            {/* Contact avatar + name */}
            <div className="flex items-center gap-3 mb-3">
              {match.candidate_avatar_url ? (
                <img
                  src={match.candidate_avatar_url}
                  alt={match.candidate_contact_name}
                  className="w-16 h-16 rounded-full object-cover border border-stone-200"
                />
              ) : (
                <ContactInitials name={match.candidate_contact_name} />
              )}
              <div>
                <p className="font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {match.candidate_contact_name}
                </p>
              </div>
            </div>

            {/* Badges */}
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <ConfidenceBadge confidence={match.confidence} />
              {match.auto_suggested && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  <Zap className="w-3 h-3" />
                  Auto-suggested
                </span>
              )}
            </div>
          </div>

          {/* Action buttons or status badge */}
          <div className="flex items-center gap-2 mt-auto">
            {isPending && (
              <>
                <button
                  onClick={onApprove}
                  disabled={!!actionLoading}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 transition-colors"
                >
                  {actionLoading === 'approve' ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                  Approve
                </button>
                <button
                  onClick={onReject}
                  disabled={!!actionLoading}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 transition-colors"
                >
                  {actionLoading === 'reject' ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                  Reject
                </button>
              </>
            )}
            {isApproved && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-100 text-green-800 rounded-lg text-sm font-medium">
                <CheckCircle className="w-4 h-4" />
                Approved
              </span>
            )}
            {isRejected && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-100 text-red-800 rounded-lg text-sm font-medium">
                <XCircle className="w-4 h-4" />
                Rejected
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
